import React, { useState, useCallback } from "react";
import { FiSearch } from "react-icons/fi";
import { TableColumnFilter } from "newui/components/Filters";
import type { FilterOption } from "newui/components/Filters";
import type {
	MySuppliersFilters,
	PerformanceBand,
} from "../../types/supplier-performance.types";
import debounce from "lodash.debounce";

interface MySuppliersListToolbarProps {
	filters: MySuppliersFilters;
	onFiltersChange: (filters: Partial<MySuppliersFilters>) => void;
}

const SUPPLIER_TYPE_OPTIONS: FilterOption<string>[] = [
	{ label: "Head Contractor", value: "head_contractor" },
	{ label: "Subcontractor", value: "subcontractor" },
	{ label: "Consultant", value: "consultant" },
];

const PERFORMANCE_BAND_OPTIONS: FilterOption<PerformanceBand>[] = [
	{ label: "Exemplary (8-9)", value: "green" },
	{ label: "Strong (6-7)", value: "blue" },
	{ label: "Moderate (3-5)", value: "amber" },
	{ label: "Low (1-2)", value: "red" },
];

const SORT_OPTIONS: FilterOption<string>[] = [
	{ label: "Name (A-Z)", value: "name:asc" },
	{ label: "Name (Z-A)", value: "name:desc" },
	{ label: "Score (High to Low)", value: "average_score:desc" },
	{ label: "Score (Low to High)", value: "average_score:asc" },
	{ label: "Last Engaged (Recent)", value: "last_engaged:desc" },
	{ label: "Engagements (Most)", value: "engagement_count:desc" },
];

export const MySuppliersListToolbar: React.FC<MySuppliersListToolbarProps> = ({
	filters,
	onFiltersChange,
}) => {
	const [searchInput, setSearchInput] = useState(filters.search);

	const debouncedSearch = useCallback(
		debounce((value: string) => {
			onFiltersChange({ search: value, page: 1 });
		}, 300),
		[onFiltersChange]
	);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInput(e.target.value);
		debouncedSearch(e.target.value);
	};

	const handleSupplierTypeChange = (values: string[]) => {
		onFiltersChange({ supplierType: values, page: 1 });
	};

	const handleBandChange = (values: PerformanceBand[]) => {
		onFiltersChange({ performanceBand: values, page: 1 });
	};

	const handleActiveToggle = () => {
		const nextValue =
			filters.isActive === null ? true : filters.isActive ? false : null;
		onFiltersChange({ isActive: nextValue, page: 1 });
	};

	const handleSortChange = (values: string[]) => {
		if (values.length > 0) {
			const [sortBy, sortOrder] = values[0].split(":") as [
				MySuppliersFilters["sortBy"],
				MySuppliersFilters["sortOrder"],
			];
			onFiltersChange({ sortBy, sortOrder, page: 1 });
		}
	};

	const currentSort = `${filters.sortBy}:${filters.sortOrder}`;

	return (
		<div
			className="flex flex-wrap items-center gap-3 rounded-lg bg-white p-4"
			data-testid="my-suppliers-toolbar"
		>
			<div className="relative min-w-[200px] flex-1">
				<FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
				<input
					type="text"
					value={searchInput}
					onChange={handleSearchChange}
					placeholder="Search suppliers..."
					className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
					data-testid="my-suppliers-search"
				/>
			</div>

			<TableColumnFilter
				label="Type"
				options={SUPPLIER_TYPE_OPTIONS}
				value={filters.supplierType}
				onChange={handleSupplierTypeChange}
				isMulti
			/>

			<TableColumnFilter
				label="Performance"
				options={PERFORMANCE_BAND_OPTIONS}
				value={filters.performanceBand}
				onChange={handleBandChange}
				isMulti
			/>

			<button
				type="button"
				onClick={handleActiveToggle}
				className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
					filters.isActive === true
						? "border-blue-500 bg-blue-50 text-blue-700"
						: filters.isActive === false
							? "border-gray-500 bg-gray-50 text-gray-700"
							: "border-gray-300 bg-white text-gray-600"
				}`}
				data-testid="my-suppliers-active-toggle"
			>
				{filters.isActive === true
					? "Active Only"
					: filters.isActive === false
						? "Inactive Only"
						: "All Status"}
			</button>

			<TableColumnFilter
				label="Sort"
				options={SORT_OPTIONS}
				value={[currentSort]}
				onChange={handleSortChange}
			/>
		</div>
	);
};
